import { makeObservable, action, observable, computed, flow } from 'mobx';
import type { ChangeEvent } from 'react';

import { saveVersion } from 'src/api/version';
import { BaseApiError } from 'src/errors';
import { hasValue } from 'src/shared/utils/common';
import type { DraftsStore } from 'src/store/drafts/drafts-store';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { PlanVersionStore } from 'src/store/plan-version';

import { ImportJob } from '../jobs/import-job';
import type { JobsStore } from '../jobs/jobs-store';

import type { TPlanVersion, TActualPlansGroups, TArchivedPlansGroup } from './types';

export class ComparisonStore {
  private readonly planVersionStore: PlanVersionStore;
  private readonly draftStore: DraftsStore;
  private readonly notifications: NotificationsStore;
  private readonly jobsStore: JobsStore;

  @observable firstTargetPlan: TPlanVersion | null;
  @observable secondTargetPlan: TPlanVersion | null;
  @observable searchingValue: string;
  @observable isComparing: boolean;
  @observable isImportOccurred: boolean;
  @observable isCompareSidebarOpened: boolean;
  @observable isActual: boolean;
  @observable isLoading = false;

  constructor(
    planVersionStore: PlanVersionStore,
    draftsStore: DraftsStore,
    notifications: NotificationsStore,
    jobsStore: JobsStore,
  ) {
    this.planVersionStore = planVersionStore;
    this.jobsStore = jobsStore;
    this.searchingValue = '';
    this.firstTargetPlan = null;
    this.secondTargetPlan = null;
    this.isComparing = false;
    this.isImportOccurred = false;
    this.isCompareSidebarOpened = false;
    this.isActual = true;
    this.draftStore = draftsStore;
    this.notifications = notifications;

    makeObservable(this);
  }

  @action.bound
  setFirstPlan(plan: TPlanVersion | null) {
    this.firstTargetPlan = plan;
  }

  @action.bound
  setSecondPlan(plan: TPlanVersion | null) {
    this.secondTargetPlan = plan;
  }

  @action.bound
  setPlanForComparison(planVersion: TPlanVersion) {
    if (this.firstTargetPlan?.id === planVersion.id) {
      this.setFirstPlan(null);
      return;
    }
    if (this.secondTargetPlan?.id === planVersion.id) {
      this.setSecondPlan(null);
      return;
    }
    if (!this.firstTargetPlan) {
      this.setFirstPlan(planVersion);
      return;
    }
    if (!this.secondTargetPlan) {
      this.setSecondPlan(planVersion);
      return;
    }
  }

  @action.bound
  toggleActual() {
    this.isActual = !this.isActual;
  }

  @action.bound
  openSidebar() {
    this.isCompareSidebarOpened = true;
  }

  @action.bound
  closeSidebar() {
    this.isCompareSidebarOpened = false;
    if (!this.isComparing) {
      this.firstTargetPlan = null;
      this.secondTargetPlan = null;
    }
  }

  @action.bound
  submitComparing() {
    this.isCompareSidebarOpened = false;
    this.isComparing = true;
  }

  @action.bound
  submitComparingWithImport(firstPlan: TPlanVersion, secondPlan: TPlanVersion) {
    this.submitComparing();
    this.setFirstPlan(firstPlan);
    this.setSecondPlan(secondPlan);
    this.isImportOccurred = true;
  }

  @action.bound
  completeComparing() {
    this.isComparing = false;
    this.isImportOccurred = false;
    this.firstTargetPlan = null;
    this.secondTargetPlan = null;
  }

  @computed
  get sidebarData() {
    const lowerSearchingValue = this.searchingValue.toLowerCase();

    if (this.isActual) {
      const filteredActualData: TActualPlansGroups[] = [];

      for (const group of this.planVersionStore.actualData) {
        const filteredPlans = group.plans.filter((plan) => plan.data.name.toLowerCase().includes(lowerSearchingValue));

        if (filteredPlans.length) {
          filteredActualData.push({
            ...group,
            plans: filteredPlans,
          });
        }
      }

      return filteredActualData;
    } else {
      const filteredArchivedData: TArchivedPlansGroup[] = [];

      for (const group of this.planVersionStore.archivedData) {
        const filteredMonths: TArchivedPlansGroup['months'] = [];

        group.months.forEach((month) => {
          const filteredPlans = month.plans.filter((plan) =>
            plan.data.name.toLowerCase().includes(lowerSearchingValue),
          );

          if (filteredPlans.length) {
            filteredMonths.push({
              ...month,
              plans: filteredPlans,
            });
          }
        });

        if (filteredMonths.length) {
          filteredArchivedData.push({
            ...group,
            months: filteredMonths,
          });
        }
      }

      return filteredArchivedData;
    }
  }

  @action.bound
  searchPlan(event: ChangeEvent<HTMLInputElement>) {
    this.searchingValue = event.target.value.toLowerCase();
  }

  @flow.bound
  async *cancelImport() {
    try {
      const importJob = this.jobsStore.activeJobs.find(
        (j) =>
          j instanceof ImportJob &&
          hasValue(j.result?.planVersionId) &&
          j.result.planVersionId === this.secondTargetPlan?.id,
      );

      if (importJob) {
        this.jobsStore.cancelJob(importJob.id);
      }

      if (!this.isImportOccurred || !this.secondTargetPlan) {
        return;
      }

      await this.draftStore.deleteDraftById(this.secondTargetPlan.id);
      await this.draftStore.fetchDraftsList();
      yield;

      this.completeComparing();
    } catch (error) {
      yield;

      console.error(error);
      this.notifications.showErrorMessageT('errors:failedToDeletePlanVersion');
    }
  }

  @flow.bound
  async *publishImportVersion() {
    try {
      const importJob = this.jobsStore.activeJobs.find(
        (j) =>
          j instanceof ImportJob &&
          hasValue(j.result?.planVersionId) &&
          j.result.planVersionId === this.secondTargetPlan?.id,
      );

      if (importJob) {
        this.jobsStore.cancelJob(importJob.id);
      }

      if (!this.isImportOccurred || !this.secondTargetPlan || !this.firstTargetPlan) {
        return;
      }

      this.isLoading = true;

      await saveVersion(this.secondTargetPlan.id, this.firstTargetPlan.id);
      await this.draftStore.deleteDraftById(this.secondTargetPlan.id);
      yield;

      this.completeComparing();

      await Promise.all([this.planVersionStore.reloadVersions(false), this.draftStore.fetchDraftsList()]);
      yield;
    } catch (error) {
      yield;

      if (error instanceof BaseApiError && error.responseMessage) {
        this.notifications.showErrorMessage(error.responseMessage);
        return;
      }

      console.error(error);
      this.notifications.showErrorMessageT('errors:failedToPublishVersion');
    } finally {
      this.isLoading = false;
    }
  }
}
